const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7016/api';

export interface AppointmentTypeTranslation {
  title?: string;
  description?: string;
  priceInfo?: string;
}

export interface AppointmentType {
  id: string;
  durationMinutes: number;
  mode?: string;
  areaOfLaw?: string;
  colorHex?: string;
  translations: Record<string, AppointmentTypeTranslation>;
}

export interface AvailabilityDay {
  date: string; // yyyy-MM-dd, local date
  slots: string[]; // ISO-8601 with offset
}

export interface AvailabilityResponse {
  timeZoneId: string;
  appointmentTypeId: string;
  durationMinutes: number;
  days: AvailabilityDay[];
}

export interface BookingCreateRequest {
  appointmentTypeId: string;
  startUtc: string; // ISO-8601
  name: string;
  email: string;
  phone?: string;
  message?: string;
  preferredLanguage?: string;
  consent: boolean;
  website?: string; // honeypot — must stay empty
}

export interface BookingCreateResponse {
  id: string;
  status: 'pending' | 'confirmed';
  startUtc: string;
  endUtc: string;
  timeZoneId: string;
  date: string;
  cancelToken: string;
}

export interface BookingSummary {
  id: string;
  status: string;
  startUtc: string;
  endUtc: string;
  timeZoneId: string;
  appointmentTypeId: string;
}

class BookingApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function parseErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    if (typeof data?.error === 'string') return data.error;
    if (Array.isArray(data?.errors)) return data.errors.join(' ');
  } catch {
    // response wasn't JSON — fall through to the generic message
  }
  return fallback;
}

/**
 * GET wrapper that retries once on a 5xx response after a short delay. Covers the Azure Functions
 * cold-start window ("Function host is not running.") where the very first request to a freshly
 * started instance fails but a retry a moment later succeeds. Only used for idempotent GETs.
 */
async function fetchJsonWithRetry(url: string): Promise<Response> {
  const response = await fetch(url);
  if (response.ok || response.status < 500) return response;
  await new Promise((resolve) => setTimeout(resolve, 1500));
  return fetch(url);
}

/**
 * Published appointment types (durations, per-language display info).
 */
export async function getAppointmentTypes(): Promise<AppointmentType[]> {
  const response = await fetchJsonWithRetry(`${API_URL}/booking/appointment-types`);
  if (!response.ok) throw new BookingApiError('Failed to load appointment types.', response.status);
  return response.json();
}

/**
 * Bookable days (with at least one free slot) for an appointment type in a local-date range.
 */
export async function getAvailability(typeId: string, from: string, to: string): Promise<AvailabilityResponse> {
  const params = new URLSearchParams({ typeId, from, to });
  const response = await fetchJsonWithRetry(`${API_URL}/booking/availability?${params.toString()}`);
  if (!response.ok) throw new BookingApiError('Failed to load availability.', response.status);
  return response.json();
}

/**
 * Creates a booking for a candidate slot. Throws BookingApiError with status 409 when the slot was
 * just taken, or 503 when Outlook availability could not be confirmed (safe to retry shortly).
 */
export async function createBooking(payload: BookingCreateRequest): Promise<BookingCreateResponse> {
  const response = await fetch(`${API_URL}/booking`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new BookingApiError(await parseErrorMessage(response, 'Booking failed.'), response.status);
  }
  return response.json();
}

export async function getBookingSummary(id: string, token: string, date: string): Promise<BookingSummary> {
  const params = new URLSearchParams({ token, d: date });
  const response = await fetch(`${API_URL}/booking/${encodeURIComponent(id)}?${params.toString()}`);
  if (!response.ok) throw new BookingApiError(await parseErrorMessage(response, 'Booking not found.'), response.status);
  return response.json();
}

export async function cancelBooking(id: string, token: string, date: string): Promise<{ id: string; status: string }> {
  const params = new URLSearchParams({ token, d: date });
  const response = await fetch(`${API_URL}/booking/${encodeURIComponent(id)}/cancel?${params.toString()}`, {
    method: 'POST',
  });
  if (!response.ok) throw new BookingApiError(await parseErrorMessage(response, 'Cancellation failed.'), response.status);
  return response.json();
}

export { BookingApiError };
